<template>
  <Paginator
    class="mt-4"
    v-show="is_visible"
    v-bind="$attrs"
    :first="(pagination_safe.page - 1) * pagination_safe.per_page"
    :rows="pagination_safe.per_page"
    :totalRecords="pagination_safe.total_count"
    :rowsPerPageOptions="rows_per_page_options"
    :showCurrentPageReport="show_total_count"
    :currentPageReportTemplate="total_count_template"
    :template="paginator_template"
    :pt="paginator_pt"
    @page="handlePage"
  />
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMqService } from '@/plugins/mq';

const DEFAULT_PER_PAGE = 10;
const DEFAULT_ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 30, 50, 100];

const props = defineProps({
  pagination: { type: Object, required: true },
  rowsPerPageOptions: { type: Array, default: null },
  hideRowsPerPageOptions: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  showTotalCount: { type: Boolean, default: true },
});

const emit = defineEmits(['update:pagination', 'change']);

const mq = useMqService();

const pagination_safe = computed(() => ({
  page: props.pagination.page || 1,
  per_page: props.pagination.per_page || DEFAULT_PER_PAGE,
  total_pages: props.pagination.total_pages || 0,
  total_count: props.pagination.total_count || 0,
}));

const { t } = useI18n();

const rows_per_page_options = computed(() => {
  if (mq.isMobile || props.hideRowsPerPageOptions) {
    return [];
  }

  const base_options = props.rowsPerPageOptions || DEFAULT_ROWS_PER_PAGE_OPTIONS;
  const options = new Set([...base_options, pagination_safe.value.per_page]);
  return Array.from(options).sort((a, b) => a - b);
});

const show_total_count = computed(() => props.showTotalCount);
const total_count_template = computed(() => {
  if (mq.isMobile) {
    return '{currentPage}';
  }

  if (pagination_safe.value.total_pages > 2) {
    return t('pagination.report', {
      first: '{first}',
      last: '{last}',
      total: '{totalRecords}',
    });
  }

  return t('pagination.total', { total: '{totalRecords}' });
});
const paginator_template = computed(() => {
  if (pagination_safe.value.total_pages > 1) {
    if (mq.isMobile) {
      return 'FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink';
    }

    return 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport';
  }

  return 'CurrentPageReport';
});

const paginator_pt = computed(() => ({
  root: {
    class: mq.isMobile
      ? 'flex flex-wrap items-center justify-center gap-x-1 gap-y-2'
      : "flex flex-wrap items-center gap-x-2 gap-y-2 after:content-[''] after:basis-full after:order-2",
  },
  first: { class: 'order-1' },
  prev: { class: 'order-1' },
  pages: { class: 'order-1' },
  next: { class: mq.isMobile ? 'order-3' : 'order-1' },
  last: { class: mq.isMobile ? 'order-3' : 'order-1' },
  current: { class: 'order-2' },
  rowsPerPageDropdown: { class: 'order-3' },
}));

const is_visible = computed(() => {
  if (props.loading) {
    return false;
  }

  return pagination_safe.value.total_pages > 1 || (show_total_count.value && pagination_safe.value.total_count > 0);
});

const handlePage = (event) => {
  const pagination_next = {
    ...props.pagination,
    page: event.page + 1,
    per_page: event.rows,
  };

  emit('update:pagination', pagination_next);
  emit('change', pagination_next);
};
</script>
